import { makeAutoObservable, runInAction } from "mobx";
import { Guide } from "@common/models";
import type { PyneSettings, DefaultPyneSettings } from "./global";
import { log } from "./lib/log";
import { SyncManager } from "./lib/SyncManager";
import { TourAnalyticsCollector } from "./lib/tour/tourAnalyticsCollector";
import { TourLifecycleManager } from "./lib/tour/tourLifecycleManager";
import { TourPlaybackManager } from "./lib/tour/tourPlayback";
import { PyneClient } from "./PyneClient";
import { TargetingEvaluator } from "./stores/TargetingEvaluator";
import { Scheduler } from "./lib/scheduler/scheduler";
import { createLocalStorageHandler, createSessionStorageHandler } from "./lib/storage";
import { PersistentStorage } from "./lib/tour/persistent-storage/storage";
import { AvatarPositioner } from "./stores/AvatarPositioner";
import { TourStartManager } from "./lib/tour/tourStartManager";
import { TabManager } from "./lib/tour/tabManager";
import { DebuggerManager } from "./stores/DebuggerManager";
import { WidgetPositioner } from "./stores/PromptPositioner";
import { GuideRegistry } from "./stores/GuideRegistry";
import { AssetManager } from "./AssetManager";
import invariant from "ts-invariant";
import { TourSessionManager } from "./lib/tour/tourSessionManager";
import { PreviewManager } from "./stores/PreviewManager";
import { ChecklistRegistry } from "./stores/ChecklistRegistry";
import { Checklist } from "@common/schemas/checklist";
import { ChecklistManager } from "./lib/checklist/checklistManager";
import { SessionRecorder } from "./lib/sessionRecorder.ts";

const defaultPyneSettings: DefaultPyneSettings = {
  api_base: "https://dashboard.pyne.ai/api",
  recordSession: true,
  sessionRecordingApiBase: "https://session-recorder.pyne.ai/api",
};

export class RootStore {
  pyneClient: PyneClient;
  currentGuide?: Guide;
  currentTour?: TourPlaybackManager;
  currentChecklistManager?: ChecklistManager;
  currentChecklist?: Checklist;
  pyneSettings: PyneSettings;
  urlPreviewGuideId?: string;
  urlPreviewChecklistId?: string;
  sharedGuideId?: string;
  isMuted = false;
  showCaptions = true;
  isExtensionSelectorPickerVisible = false;
  syncManager: SyncManager;
  tourLifecycleManager!: TourLifecycleManager;
  targetingEvaluator!: TargetingEvaluator;
  scheduler!: Scheduler;
  tourStartManager!: TourStartManager;
  tabManager!: TabManager;
  tourSessionManager!: TourSessionManager;
  avatarPosition = new AvatarPositioner(this);
  widgetPosition = new WidgetPositioner(this);
  debuggerManager!: DebuggerManager;
  guideRegistry: GuideRegistry;
  checklistRegistry: ChecklistRegistry;
  storage: PersistentStorage;
  public isPreview = false;
  assetManager = new AssetManager(this);
  previewManager!: PreviewManager;
  sessionRecorder?: SessionRecorder;

  constructor() {
    makeAutoObservable(this);

    this.pyneClient = new PyneClient(this);

    this.pyneSettings = { ...defaultPyneSettings };
    this.guideRegistry = new GuideRegistry();
    this.checklistRegistry = new ChecklistRegistry();
    this.syncManager = new SyncManager({
      rootStore: this,
      pyneClient: this.pyneClient,
      guideRegistry: this.guideRegistry,
      checklistRegistry: this.checklistRegistry,
    });
    this.storage = new PersistentStorage({
      localStorageHandler: createLocalStorageHandler(),
      sessionStorageHandler: createSessionStorageHandler(),
    });

    const previewGuideId = new URLSearchParams(location.search).get("pyne-guide-id") ?? undefined;
    const existingPreviewGuideId = this.storage.previewState.getGuideId();
    const previewGuideIdToBeStarted = previewGuideId ?? existingPreviewGuideId;
    this.urlPreviewGuideId = previewGuideIdToBeStarted;
    if (previewGuideIdToBeStarted) {
      this.isPreview = true;
      this.storage.enablePreviewMode();
      this.storage.previewState.enableGuidePreview(previewGuideIdToBeStarted);
      this.storage.debuggerState.enable();
    }

    const previewChecklistId = new URLSearchParams(location.search).get("pyne-checklist-id") ?? undefined;
    const existingPreviewChecklistId = this.storage.previewState.getChecklistId();
    const previewChecklistIdToBeStarted = previewChecklistId ?? existingPreviewChecklistId;
    this.urlPreviewChecklistId = previewChecklistIdToBeStarted;
    if (previewChecklistIdToBeStarted) {
      this.isPreview = true;
      this.storage.enablePreviewMode();
      this.storage.previewState.enableChecklistPreview(previewChecklistIdToBeStarted);
      this.storage.debuggerState.enable();
    }

    const sharedGuideId = new URLSearchParams(location.search).get("pyne-shared-guide-id") ?? undefined;
    this.sharedGuideId = sharedGuideId;

    this.handleExtensionMessages = this.handleExtensionMessages.bind(this);
    window.addEventListener("message", this.handleExtensionMessages);
  }

  private handleExtensionMessages(event: MessageEvent): void {
    if (event.data && event.data.type === "pyne-extension/selector-picker-visibility-changed") {
      const visible = !!event.data.visible;
      runInAction(() => {
        this.isExtensionSelectorPickerVisible = visible;
        log(`Pyne widget: Extension selector picker visibility changed to ${visible}`);
      });
    }
  }

  setPyneSettings(pyneSettings: PyneSettings) {
    this.pyneSettings = { ...defaultPyneSettings, ...pyneSettings };
    validateApiUrl(this.pyneSettings.api_base);
    validateSessionRecordingApiUrl(this.pyneSettings.recordSession, this.pyneSettings.sessionRecordingApiBase);
  }

  init() {
    if (!this.pyneSettings.api_base || !this.pyneSettings.app_id) {
      log("Pyne settings not found");
      return;
    }

    this.tourStartManager = new TourStartManager();
    this.targetingEvaluator = new TargetingEvaluator({
      syncManager: this.syncManager,
      storage: this.storage,
      guideRegistry: this.guideRegistry,
      checklistRegistry: this.checklistRegistry,
    });
    this.tourSessionManager = new TourSessionManager({
      tourStateStorage: this.storage.tourState,
      sessionStateStorage: this.storage.sessionState,
    });
    this.scheduler = new Scheduler({
      targetingEvaluator: this.targetingEvaluator,
      pyneClient: this.pyneClient,
      syncManager: this.syncManager,
      guideRegistry: this.guideRegistry,
      checklistRegistry: this.checklistRegistry,
      storage: this.storage,
      tourSessionManager: this.tourSessionManager,
      options: {
        previewGuideId: this.urlPreviewGuideId,
        sharedGuideId: this.sharedGuideId,
        previewOnly: this.pyneSettings.previewOnly,
        previewChecklistId: this.urlPreviewChecklistId,
      },
    });
    this.tabManager = new TabManager({
      tabState: this.storage.tabState,
      tourState: this.storage.tourState,
    });
    this.tourLifecycleManager = new TourLifecycleManager({
      tabManager: this.tabManager,
      tourStartManager: this.tourStartManager,
      scheduler: this.scheduler,
      storage: this.storage,
      tourSessionManager: this.tourSessionManager,
      skipBlobMediaPreload: this.pyneSettings.skipBlobMediaPreload,
      mediaPlaybackRate: this.pyneSettings.mediaPlaybackRate,
      pyneSettings: this.pyneSettings,
      guideRegistry: this.guideRegistry,
    });

    if (this.isPreview && (this.urlPreviewGuideId || this.urlPreviewChecklistId)) {
      this.previewManager = new PreviewManager({
        storage: this.storage,
        tourLifecycleManager: this.tourLifecycleManager,
      });
    }

    TourAnalyticsCollector.init({
      appId: this.pyneSettings.app_id,
      apiBaseUrl: this.pyneSettings.api_base,
      isPreview: this.isPreview,
      skipTracking: this.pyneSettings.skipTracking,
      getUser: () => {
        return {
          user_id: this.pyneClient.currentUser?.id,
          user_traits: this.pyneClient.currentUser?.traits,
          anonymous_id: this.pyneClient.anonymousId ?? undefined,
        };
      },
    });

    // Session replay should only be active while a guide is playing.
    if (!!this.pyneSettings.recordSession) {
      this.sessionRecorder = new SessionRecorder(
        this.pyneSettings.sessionRecordingApiBase ?? "",
        this.pyneSettings.app_id ?? ""
      );

      // Start recording when a tour is initialized
      this.tourSessionManager.events.on("tourSessionStarted", ({ payload: { sessionId } }) => {
        this.sessionRecorder?.start(sessionId);
      });

      this.tourSessionManager.events.on("tourSessionResumed", ({ payload: { sessionId } }) => {
        this.sessionRecorder?.start(sessionId);
      });

      // Stop recording when the tour ends for any reason
      this.tourLifecycleManager.events.on("tourDismissed", () => {
        this.sessionRecorder?.stop();
      });
      this.tourLifecycleManager.events.on("tourFinished", () => {
        this.sessionRecorder?.stop();
      });
      this.tourLifecycleManager.events.on("tourTerminated", () => {
        this.sessionRecorder?.stop();
      });
    }

    const isDebugOptionNotSpecified = this.pyneSettings.debug === undefined;
    if (isDebugOptionNotSpecified) {
      this.pyneSettings.debug = this.storage.debuggerState.isEnabled;
    } else if (this.pyneSettings.debug) {
      this.storage.debuggerState.enable();
    }

    this.debuggerManager = new DebuggerManager({
      tourLifecycleManager: this.tourLifecycleManager,
      pyneClient: this.pyneClient,
      storage: this.storage.debuggerState,
      pyneSettings: this.pyneSettings,
      guideRegistry: this.guideRegistry,
      targetingEvaluator: this.targetingEvaluator,
      userStateStorage: this.storage.userState,
      checklistRegistry: this.checklistRegistry,
    });

    this.tourLifecycleManager.events.on("tourInitialized", ({ payload: { guide, tour } }) => {
      runInAction(() => {
        log("Tour initialized", tour);
        this.currentGuide = guide;
        this.currentTour = tour;
      });
    });

    this.tourLifecycleManager.events.on("tourDismissed", () => {
      runInAction(() => {
        this.currentGuide = undefined;
        this.currentTour = undefined;
      });
    });

    this.tourLifecycleManager.events.on("tourFinished", () => {
      runInAction(() => {
        this.currentGuide = undefined;
        this.currentTour = undefined;
      });
    });

    this.tourLifecycleManager.events.on("tourTerminated", () => {
      runInAction(() => {
        this.currentGuide = undefined;
        this.currentTour = undefined;
      });
    });

    this.tourLifecycleManager.events.on("checklistInitialized", ({ payload: { checklist, checklistManager } }) => {
      runInAction(() => {
        this.currentChecklistManager = checklistManager;
        this.currentChecklist = checklist;
      });
    });

    this.tourLifecycleManager.events.on("checklistDismissed", () => {
      runInAction(() => {
        this.currentChecklistManager = undefined;
        this.currentChecklist = undefined;
      });
    });

    this.tourLifecycleManager.events.on("checklistCompleted", () => {
      runInAction(() => {
        this.currentChecklistManager = undefined;
        this.currentChecklist = undefined;
      });
    });

    this.tourLifecycleManager.events.on("checklistTerminated", () => {
      runInAction(() => {
        this.currentChecklistManager = undefined;
        this.currentChecklist = undefined;
      });
    });

    this.tourLifecycleManager.start();

    this.assetManager.init();
  }

  dismissCurrentTour() {
    this.tourLifecycleManager.dismissCurrentTour();
  }

  clearState() {
    this.tourLifecycleManager?.stop();
    this.storage.clearAll();
    window.removeEventListener("message", this.handleExtensionMessages);
  }

  get isDebugVisible() {
    return this.pyneSettings.debug && !this.isExtensionSelectorPickerVisible;
  }

  enableDebug() {
    this.pyneSettings.debug = true;
    this.storage.debuggerState.enable();
  }

  disableDebug() {
    this.pyneSettings.debug = false;
    this.storage.debuggerState.disable();
  }

  disablePreview() {
    this.isPreview = false;
  }

  setAvatarInitialPositionRight() {
    this.avatarPosition.isPositionedRight = true;
  }

  dispose() {
    window.removeEventListener("message", this.handleExtensionMessages);
    this.tourLifecycleManager?.stop();
    // Ensure session replay is stopped on dispose
    this.sessionRecorder?.stop();
    log("Pyne widget: RootStore disposed.");
  }
}

export const store = new RootStore();

function validateApiUrl(apiBase: string | undefined) {
  invariant(apiBase, "API base URL is not set");

  const allowedOrigins = ["https://dashboard.pyne.ai", "http://localhost:3000", "https://test.pyne.ai"];
  const origin = new URL(apiBase).origin;
  invariant(allowedOrigins.includes(origin), `API base URL ${apiBase} is not allowed`);
}

function validateSessionRecordingApiUrl(sessionRecordingEnabled?: boolean, sessionRecordingApiBase?: string) {
  if (!sessionRecordingEnabled) {
    return;
  }

  invariant(sessionRecordingApiBase, "Session recording API base URL is not set");

  const allowedOrigins = ["https://session-recorder.pyne.ai", "http://localhost:8080"];
  const origin = new URL(sessionRecordingApiBase).origin;
  invariant(
    allowedOrigins.includes(origin),
    `Session recording API base URL ${sessionRecordingApiBase} is not allowed`
  );
}
